import React, { useEffect, useRef, useState } from 'react';
import logo from '../components/images/sodtf logo dark stacked.png';
import { useNavigate, Link } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { Container, Typography, TextField, Button, Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const textFieldSx = {
  backgroundColor: 'white',
  borderRadius: 0,
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
};

const buttonSx = {
  borderColor: 'rgb(167, 198, 103)',
  borderWidth: '2.5px',
  color: 'rgb(167, 198, 103)',
  borderRadius: '15px',
  textTransform: 'none',
  marginTop: 4,
  width: '15rem',
  alignSelf: 'center',
  fontWeight: '500',
  fontSize: '1.1rem',
  py: 0.75,
  '&:hover': {
    borderColor: 'rgb(157, 201, 70))',
    color: 'rgb(157, 201, 70)',
    backgroundColor: 'rgba(157, 201, 70, 0.57))',
  },
};

function Login() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stage, setStage] = useState('credentials');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const [mfaPhone, setMfaPhone] = useState('');
  const [mfaSending, setMfaSending] = useState(false);
  const [mfaVerifying, setMfaVerifying] = useState(false);

  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const returnUrl = params.get('returnUrl');
  const recaptchaVerifierRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (recaptchaVerifierRef.current) {
      return;
    }
    const verifier = new RecaptchaVerifier(auth, 'login-recaptcha', {
      size: 'invisible',
    });
    verifier.render().catch(() => { });
    recaptchaVerifierRef.current = verifier;
    return () => {
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
    };
  }, []);

  const finishLogin = async (userCredential) => {
    const user = userCredential.user;
    console.log('[Login] emailVerified state upon sign-in:', user.emailVerified);
    await user.getIdToken(true);
    const token = await user.getIdToken();
    localStorage.setItem('token', token);

    await new Promise((resolve) => setTimeout(resolve, 500));

    const db = getFirestore();
    const userDocRef = doc(db, 'users', user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists() && userDocSnap.data().role === 1) {
      const grantAdmin = httpsCallable(functions, 'grantAdminClaimIfEligible');
      try {
        await grantAdmin();
      } catch (claimErr) {
        console.warn('Failed to grant admin claim:', claimErr);
      }
    }

    try {
      const data = userDocSnap.exists() ? userDocSnap.data() : {};
      const needsClear = Boolean(data?.requireEmailReverify);
      if (needsClear) {
        await updateDoc(userDocRef, { requireEmailReverify: false, emailVerified: true });
      } else {
        await updateDoc(userDocRef, { emailVerified: true });
      }
    } catch (clearErr) {
      console.warn('Failed to sync verification status to user doc:', clearErr);
    }

    const destination = returnUrl || '/';
    if (destination.startsWith('http')) {
      window.location.href = destination;
      return;
    }
    navigate(destination);
  };

  const beginMultiFactorSignIn = async (resolver, keepStage = false) => {
    const verifier = recaptchaVerifierRef.current;
    if (!verifier) {
      throw new Error('Security verification is not ready. Refresh the page and try again.');
    }
    if (!resolver?.hints?.length) {
      throw new Error('No multi-factor options are configured for this account.');
    }
    try {
      if (!keepStage) {
        setStage('mfa');
        setError('');
      }
      setMfaSending(true);
      setMfaError('');
      setMfaCode('');
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const selectedHint = resolver.hints[0];
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: selectedHint, session: resolver.session },
        verifier
      );
      setMfaVerificationId(verificationId);
      setMfaResolver(resolver);
      setMfaPhone(selectedHint?.phoneNumber || 'your phone');
    } catch (mfaErr) {
      if (!keepStage) {
        setStage('credentials');
      }
      throw mfaErr;
    } finally {
      setMfaSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (stage !== 'credentials') return;
    setError('');
    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await finishLogin(userCredential);
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        try {
          await beginMultiFactorSignIn(err.resolver);
        } catch (startErr) {
          setError(startErr?.message || 'Unable to start two-factor sign-in.');
        }
        return;
      }
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaResolver || !mfaVerificationId) {
      setMfaError('Your verification session expired. Please try signing in again.');
      setStage('credentials');
      return;
    }
    setMfaError('');
    setMfaVerifying(true);
    try {
      const credential = PhoneAuthProvider.credential(mfaVerificationId, mfaCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      const userCredential = await mfaResolver.resolveSignIn(assertion);
      await finishLogin(userCredential);
      setStage('credentials');
      setMfaResolver(null);
      setMfaVerificationId(null);
      setMfaPhone('');
      setMfaCode('');
    } catch (mfaErr) {
      console.error('Failed to complete MFA sign-in:', mfaErr);
      setMfaError(mfaErr?.message || 'Invalid verification code.');
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleResendMfa = async () => {
    if (!mfaResolver) return;
    try {
      await beginMultiFactorSignIn(mfaResolver, true);
    } catch (err) {
      setMfaError(err?.message || 'Unable to resend the code right now.');
    }
  };

  const handleCancelMfa = () => {
    setStage('credentials');
    setMfaResolver(null);
    setMfaVerificationId(null);
    setMfaPhone('');
    setMfaCode('');
    setMfaError('');
  };

  const renderCredentialForm = (isMobileLayout) => (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: isMobileLayout ? '80%' : '80%',
      }}
    >
      {error && (
        <Typography color="error" align="center" sx={{ width: '100%' }}>
          {error}
        </Typography>
      )}
      <TextField
        fullWidth
        label="Email"
        type="email"
        variant="outlined"
        required
        sx={textFieldSx}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
      />
      <TextField
        fullWidth
        label="Password"
        type="password"
        variant="outlined"
        required
        sx={textFieldSx}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        margin="normal"
      />
      <Button type="submit" fullWidth variant="outlined" disabled={isLoading} sx={buttonSx}>
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Log In'}
      </Button>
      <Typography
        variant="body1"
        align="center"
        sx={{
          justifySelf: 'center',
          mt: isMobileLayout ? 2 : 4,
          width: '18rem',
          color: '#5A6872',
          fontFamily: 'Anek Latin',
          textAlign: 'center',
          fontSize: '1.1rem',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        Don't have an account?
        <Button component={Link} to={returnUrl ? `/signup?returnUrl=${encodeURIComponent(returnUrl)}` : '/signup'} sx={{ m: 0, color: 'rgb(167, 198, 103)' }}>
          Sign Up
        </Button>
        to save your information for fast reorders!
      </Typography>
    </Box>
  );

  const renderMfaForm = (isMobileLayout) => (
    <Box
      component="form"
      onSubmit={handleMfaSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: isMobileLayout ? '80%' : '80%',
      }}
    >
      <Typography align="center" sx={{ color: '#5A6872', mb: 1 }}>
        Enter the 6-digit code sent to {mfaPhone || 'your phone'} to finish signing in.
      </Typography>
      {mfaError && (
        <Typography color="error" align="center" sx={{ width: '100%' }}>
          {mfaError}
        </Typography>
      )}
      <TextField
        fullWidth
        label="Verification Code"
        type="tel"
        variant="outlined"
        required
        sx={textFieldSx}
        value={mfaCode}
        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        margin="normal"
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
      />
      <Button type="submit" fullWidth variant="outlined" disabled={mfaCode.length < 6 || mfaVerifying} sx={buttonSx}>
        {mfaVerifying ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
      </Button>
      <Button
        type="button"
        variant="text"
        onClick={handleResendMfa}
        disabled={mfaSending}
        sx={{ color: '#5A6872', mt: 2 }}
      >
        {mfaSending ? 'Sending another code…' : 'Resend code'}
      </Button>
      <Button
        type="button"
        variant="text"
        onClick={handleCancelMfa}
        sx={{ color: '#5A6872' }}
      >
        Use a different account
      </Button>
    </Box>
  );

  const headingText = stage === 'mfa' ? 'Two-Factor Verification' : 'LOGIN';

  return (
    <Container
      maxWidth="lg"
      disableGutters
      sx={{
        display: 'flex',
        position: 'relative',
        margin: '0 auto',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
        padding: 5,
        width: '100vw',
        boxSizing: 'border-box',
      }}
    >
      {isMobile ? (
        <div
          id="container"
          style={{
            borderRadius: 20,
            backgroundColor: '#ffffffff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '70vh',
            width: '80vw',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
            boxShadow: '0 4px 8px rgba(179, 179, 179, 0.5)'
          }}
        >
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent', height: '100%', width: '100%' }}>
            <img src={logo} alt="See Our Designs" style={{ maxWidth: '60%', maxHeight: '60%' }} />
          </div>
          <div
            style={{
              flex: 1,
              backgroundColor: '#eaf3d4',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              fontFamily: 'Anek Latin'
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              sx={{
                fontFamily: 'Anek Latin',
                mb: 0,
                mt: 2,
                fontWeight: 'bold',
                color: '#5A6872',
                textAlign: 'center',
                width: '100%',
              }}
            >
              {headingText}
            </Typography>
            {stage === 'mfa' ? renderMfaForm(true) : renderCredentialForm(true)}
          </div>
        </div>
      ) : (
        <Box
          sx={{
            borderRadius: 5,
            backgroundColor: 'white',
            display: 'flex',
            height: '70vh',
            width: '80vw',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent' }}>
            <img src={logo} alt="See Our Designs" style={{ maxWidth: '60%', maxHeight: '60%' }} />
          </Box>
          <Box
            sx={{
              flex: 1,
              bgcolor: '#eaf3d4',
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              borderTopRightRadius: 20,
              borderBottomRightRadius: 20,
              height: '100%',
              boxSizing: 'border-box',
              minWidth: 0,
              minHeight: 0,
              fontFamily: 'Anek Latin'
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              sx={{
                fontFamily: 'Anek Latin',
                mb: 0,
                fontWeight: 'bold',
                color: '#5A6872',
                textAlign: 'center',
                width: '100%',
              }}
            >
              {headingText}
            </Typography>
            {stage === 'mfa' ? renderMfaForm(false) : renderCredentialForm(false)}
          </Box>
        </Box>
      )}
      <div id="login-recaptcha" style={{ display: 'none' }} />
    </Container>
  );
}

export default Login;
